import {Parameter} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {RelatedCustomNoticeResponseBody, RelatedCustomNoticesResourceId} from '@dms/api/core';

import {TestIdProps, useNavigate} from 'shared/web';

import {composeCustomNoticeSourcePath} from '../../utils/composeCustomNoticeSourcePath';
import {getCustomNoticeOwnershipRolesLabel} from '../../utils/getCustomNoticeOwnershipRolesLabel';
import {getCustomNoticeRelationLabel} from '../../utils/getCustomNoticeRelationLabel';
import {CustomNoticeCardBase} from '../CustomNoticeCardBase/CustomNoticeCardBase';

interface RelatedCustomNoticeCardProps extends TestIdProps {
  notice: RelatedCustomNoticeResponseBody;
  hostResourceId: RelatedCustomNoticesResourceId;
}

export function RelatedCustomNoticeCard(props: RelatedCustomNoticeCardProps) {
  const navigate = useNavigate();

  const relationLabel = getRelationLabel(props.notice, props.hostResourceId);
  const parameters = getParameters(props.notice, relationLabel, navigate);

  return (
    <CustomNoticeCardBase
      notice={props.notice}
      parameters={parameters}
      data-testid={props['data-testid']}
    />
  );
}

function getRelationLabel(
  notice: RelatedCustomNoticeResponseBody,
  hostResourceId: RelatedCustomNoticesResourceId
) {
  return match(notice.relation)
    .with('ownership', () => getCustomNoticeOwnershipRolesLabel(notice.roles))
    .otherwise((relation) => getCustomNoticeRelationLabel(relation, hostResourceId));
}

function getParameters(
  notice: RelatedCustomNoticeResponseBody,
  relationLabel: string,
  navigate: ReturnType<typeof useNavigate>
) {
  const sourcePath = composeCustomNoticeSourcePath(
    notice.source.resourceId,
    notice.source.recordId
  );

  const buildSourceLink = (path: string): Parameter => ({
    title: notice.source.label ?? relationLabel,
    variant: 'link',
    onClick: () => navigate(path),
  });

  return match({relation: notice.relation, sourcePath, label: notice.source.label})
    .with(Pattern.union({relation: 'own'}, {sourcePath: Pattern.nullish}), () => [relationLabel])
    .with({sourcePath: Pattern.string, label: Pattern.nonNullable}, (value) => [
      relationLabel,
      buildSourceLink(value.sourcePath),
    ])
    .with({sourcePath: Pattern.string}, (value) => [buildSourceLink(value.sourcePath)])
    .exhaustive();
}
